import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgentWizardTab } from "../utils/hosted-agents"
import type { HostedAgentLifecycleRequest } from "@/api/admin-api"

import { getHostedAgentConflict } from "@/api/admin-api"
import { Bot, List, Network, Plus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DeleteHostedAgentDialog } from "./delete-hosted-agent-dialog"
import { EditHostedAgentDialog } from "./edit-hosted-agent-dialog"
import { HostedAgentAlertsCard } from "./hosted-agent-alerts-card"
import { HostedAgentMap } from "./hosted-agent-map/hosted-agent-map"
import { HostedAgentsTable } from "./hosted-agents-table"
import { InstantiateHostedAgentDialog } from "./instantiate-hosted-agent-dialog"
import { RotateHostedAgentSecretDialog } from "./rotate-hosted-agent-secret-dialog"
import {
  useAdminHostedAgentActiveLifecycleQuery,
  useAdminHostedAgentsQuery,
  useAdminHostedAgentTemplatesQuery,
  useCreateHostedAgentMutation,
  useDeleteHostedAgentMutation,
  useInstantiateHostedAgentTemplateMutation,
  usePauseHostedAgentMutation,
  useResumeHostedAgentMutation,
  useRotateHostedAgentSecretMutation,
  useUpdateHostedAgentAccessMutation,
} from "../hooks/admin-queries"
import { HOSTED_AGENT_DEFAULT_BUDGET } from "../utils/hosted-agents"

type HostedAgentsView = "table" | "map"

export function HostedAgentsManagementCard() {
  const { t } = useTranslation()
  const [view, setView] = useState<HostedAgentsView>("table")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editAgentId, setEditAgentId] = useState<string | undefined>()
  const [editInitialTab, setEditInitialTab] = useState<HostedAgentWizardTab>("profile")
  const [accessAgentId, setAccessAgentId] = useState<string | undefined>()
  const [deleteAgentId, setDeleteAgentId] = useState<string | undefined>()
  const [rotateAgentId, setRotateAgentId] = useState<string | undefined>()
  const [pendingAgentId, setPendingAgentId] = useState<string | undefined>()

  // Tenant-wide active lifecycle requests (B10/B13) → one status chip per row.
  // Queried BEFORE the agent list so the list can be told that a deploy-worker
  // action is in flight and keep polling until it lands; the query itself only
  // repeats while something is actually active, so an idle tenant costs one
  // request.
  const activeLifecycleQuery = useAdminHostedAgentActiveLifecycleQuery()
  const hasActiveLifecycle = (activeLifecycleQuery.data?.length ?? 0) > 0
  const { data: agents, isLoading } = useAdminHostedAgentsQuery(true, hasActiveLifecycle)
  const { data: templates, isLoading: isLoadingTemplates } =
    useAdminHostedAgentTemplatesQuery(isCreateDialogOpen)
  const lifecycleByAgentId = useMemo(() => {
    const map = new Map<string, HostedAgentLifecycleRequest>()
    for (const request of activeLifecycleQuery.data ?? []) {
      if (!map.has(request.agent_id)) map.set(request.agent_id, request)
    }
    return map
  }, [activeLifecycleQuery.data])

  const openEditor = (agentId: string, tab: HostedAgentWizardTab = "profile") => {
    setEditInitialTab(tab)
    setEditAgentId(agentId)
  }

  const instantiateMutation = useInstantiateHostedAgentTemplateMutation()
  const createMutation = useCreateHostedAgentMutation()
  const deleteMutation = useDeleteHostedAgentMutation()
  const pauseMutation = usePauseHostedAgentMutation()
  const resumeMutation = useResumeHostedAgentMutation()
  const accessMutation = useUpdateHostedAgentAccessMutation()
  const rotateMutation = useRotateHostedAgentSecretMutation()

  const findAgent = (agentId: string | undefined) => agents?.find((agent) => agent.id === agentId)

  const accessAgent = findAgent(accessAgentId)
  const deleteAgent = findAgent(deleteAgentId)
  const rotateAgent = findAgent(rotateAgentId)

  const handleInstantiate = async (templateId: string, overrides: Record<string, unknown>) => {
    try {
      const created = await instantiateMutation.mutateAsync({
        templateId,
        overrides: { ...overrides, enabled: false },
      })
      toast.success(
        t("adminDashboard.hostedAgents.createSuccess", { defaultValue: "Hosted agent created" })
      )
      setIsCreateDialogOpen(false)
      openEditor(created.id)
    } catch (error) {
      if (getHostedAgentConflict(error)?.code !== "slug_conflict") {
        toast.error(
          t("adminDashboard.hostedAgents.createError", {
            defaultValue: "Failed to create hosted agent",
          }),
          { description: error instanceof Error ? error.message : undefined }
        )
      }
      throw error
    }
  }

  const handleCreateBlank = async ({ name, slug }: { name: string; slug: string }) => {
    try {
      // Budget invariant (HERMES risk 5): a blank agent must never be created
      // with the backend's null (= unlimited) budget defaults, and it starts
      // disabled until an admin has configured it in the wizard.
      const created = await createMutation.mutateAsync({
        slug,
        enabled: false,
        profile: { name },
        budget: { ...HOSTED_AGENT_DEFAULT_BUDGET, on_exceeded: "block" },
      })
      toast.success(
        t("adminDashboard.hostedAgents.createSuccess", { defaultValue: "Hosted agent created" })
      )
      setIsCreateDialogOpen(false)
      openEditor(created.id)
    } catch (error) {
      if (getHostedAgentConflict(error)?.code !== "slug_conflict") {
        toast.error(
          t("adminDashboard.hostedAgents.createError", {
            defaultValue: "Failed to create hosted agent",
          }),
          { description: error instanceof Error ? error.message : undefined }
        )
      }
      throw error
    }
  }

  const handlePauseToggle = async (agentId: string, paused: boolean) => {
    setPendingAgentId(agentId)
    try {
      if (paused) {
        await resumeMutation.mutateAsync(agentId)
        toast.success(
          t("adminDashboard.hostedAgents.resumeSuccess", { defaultValue: "Agent resumed" })
        )
      } else {
        await pauseMutation.mutateAsync(agentId)
        toast.success(
          t("adminDashboard.hostedAgents.pauseSuccess", {
            defaultValue: "Agent paused — new runs are held until resume",
          })
        )
      }
    } catch (error) {
      toast.error(
        paused
          ? t("adminDashboard.hostedAgents.resumeError", { defaultValue: "Failed to resume agent" })
          : t("adminDashboard.hostedAgents.pauseError", { defaultValue: "Failed to pause agent" }),
        { description: error instanceof Error ? error.message : undefined }
      )
    } finally {
      setPendingAgentId(undefined)
    }
  }

  const handleDelete = async () => {
    if (!deleteAgentId) return
    try {
      await deleteMutation.mutateAsync(deleteAgentId)
      toast.success(
        t("adminDashboard.hostedAgents.deleteSuccess", { defaultValue: "Hosted agent deleted" })
      )
      setDeleteAgentId(undefined)
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.deleteError", {
          defaultValue: "Failed to delete hosted agent",
        }),
        { description: error instanceof Error ? error.message : undefined }
      )
      throw error
    }
  }

  const handleRotate = async () => {
    if (!rotateAgentId) {
      throw new Error("No agent selected")
    }
    try {
      return await rotateMutation.mutateAsync(rotateAgentId)
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.rotateError", { defaultValue: "Failed to rotate secret" }),
        { description: error instanceof Error ? error.message : undefined }
      )
      throw error
    }
  }

  const createButton = (
    <Button
      className="flex items-center gap-2 px-3"
      size="sm"
      onClick={() => setIsCreateDialogOpen(true)}
    >
      <Plus className="size-4" />
      {t("adminDashboard.hostedAgents.addAgent", { defaultValue: "Create agent" })}
    </Button>
  )

  const headerActions = (
    <>
      <ToggleGroup
        size="sm"
        type="single"
        value={view}
        variant="outline"
        aria-label={t("adminDashboard.hostedAgents.viewToggleLabel", {
          defaultValue: "View",
        })}
        onValueChange={(value) => {
          if (value === "table" || value === "map") setView(value)
        }}
      >
        <ToggleGroupItem
          aria-label={t("adminDashboard.hostedAgents.viewTable", { defaultValue: "Table" })}
          value="table"
        >
          <List className="size-4" />
          <span className="hidden sm:inline">
            {t("adminDashboard.hostedAgents.viewTable", { defaultValue: "Table" })}
          </span>
        </ToggleGroupItem>
        <ToggleGroupItem
          aria-label={t("adminDashboard.hostedAgents.viewMap", { defaultValue: "Map" })}
          value="map"
        >
          <Network className="size-4" />
          <span className="hidden sm:inline">
            {t("adminDashboard.hostedAgents.viewMap", { defaultValue: "Map" })}
          </span>
        </ToggleGroupItem>
      </ToggleGroup>
      {createButton}
    </>
  )

  return (
    <>
      <CollapsibleManagementSection
        headerActions={headerActions}
        icon={<Bot className="size-5" />}
        id="hosted-agents"
        storageKey="admin-management-hosted-agents-expanded"
        title={t("adminDashboard.hostedAgents.title", { defaultValue: "Hosted agents" })}
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : null}
        {!isLoading && view === "map" ? (
          <HostedAgentMap agents={agents || []} emptyAction={createButton} />
        ) : null}
        {!isLoading && view === "table" ? (
          <>
            {/* #3039: open alerts first — what needs attention before the list. */}
            <HostedAgentAlertsCard agents={agents || []} />
            <HostedAgentsTable
              agents={agents || []}
              emptyAction={createButton}
              lifecycleByAgentId={lifecycleByAgentId}
              pendingAgentId={pendingAgentId}
              onDelete={(agentId) => setDeleteAgentId(agentId)}
              onEdit={(agentId) => openEditor(agentId)}
              onEditAccess={(agentId) => setAccessAgentId(agentId)}
              onOpenLifecycle={(agentId) => openEditor(agentId, "lifecycle")}
              onPauseToggle={(agentId, paused) => void handlePauseToggle(agentId, paused)}
              onRotateSecret={(agentId) => setRotateAgentId(agentId)}
            />
          </>
        ) : null}
      </CollapsibleManagementSection>

      <EditHostedAgentDialog
        agentId={editAgentId}
        initialTab={editInitialTab}
        isOpen={Boolean(editAgentId)}
        onOpenChange={(open) => {
          if (!open) setEditAgentId(undefined)
        }}
      />

      <InstantiateHostedAgentDialog
        isLoadingTemplates={isLoadingTemplates}
        isOpen={isCreateDialogOpen}
        isPending={instantiateMutation.isPending || createMutation.isPending}
        templates={templates || []}
        onCreateBlank={handleCreateBlank}
        onInstantiate={handleInstantiate}
        onOpenChange={setIsCreateDialogOpen}
      />

      <AccessPolicyDialog
        isSaving={accessMutation.isPending}
        open={Boolean(accessAgentId)}
        policy={accessAgent?.access}
        description={t("adminDashboard.hostedAgents.accessDescription", {
          defaultValue: "Control which users and groups can see and use this agent.",
        })}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: accessAgent?.profile.name ?? "",
        })}
        onOpenChange={(open) => {
          if (!open) setAccessAgentId(undefined)
        }}
        onSave={async (policy) => {
          if (!accessAgentId) return
          try {
            await accessMutation.mutateAsync({ agentId: accessAgentId, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              getHostedAgentConflict(error)?.code === "revision_conflict"
                ? t("adminDashboard.hostedAgents.revisionConflict", {
                    defaultValue: "The agent changed while you were editing — reload and retry.",
                  })
                : t("adminDashboard.access.saveError", {
                    defaultValue: "Failed to update access policy",
                  })
            )
            throw error
          }
        }}
      />

      <DeleteHostedAgentDialog
        isOpen={Boolean(deleteAgentId)}
        isPending={deleteMutation.isPending}
        agent={
          deleteAgent
            ? { id: deleteAgent.id, name: deleteAgent.profile.name, slug: deleteAgent.slug }
            : null
        }
        onConfirm={handleDelete}
        onOpenChange={(open) => {
          if (!open) setDeleteAgentId(undefined)
        }}
      />

      <RotateHostedAgentSecretDialog
        isOpen={Boolean(rotateAgentId)}
        isPending={rotateMutation.isPending}
        agent={
          rotateAgent
            ? {
                id: rotateAgent.id,
                name: rotateAgent.profile.name,
                slug: rotateAgent.slug,
                // A6: the durable `{configured, version, rotated_at}` summary,
                // so a REOPENED dialog can still say which version is live and
                // when it was rotated. The one-time secret never comes back —
                // this does.
                secret: rotateAgent.secret,
              }
            : null
        }
        onRotate={handleRotate}
        onOpenChange={(open) => {
          if (!open) {
            setRotateAgentId(undefined)
            // Drop the one-time secret from the mutation cache too — without
            // this it would stay readable (e.g. in React Query devtools) for
            // the rest of the admin session.
            rotateMutation.reset()
          }
        }}
      />
    </>
  )
}
